import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../auth/auth-context.js';
import type { AuftragsQuelle } from './runs-source.js';
import { type AuftragsZustand, useAuftraege, useGesamtAuftraege } from './use-runs.js';

/** Begruendung und Grenzen: siehe den Kopfkommentar in run-list-page.tsx (Befund H2). */
export const HOL_GRENZE = 1000;

interface Abo {
  zustand: AuftragsZustand;
  anfordern: () => void;
}

interface Wert extends Abo {
  /** Gesamtansicht der Rollen `admin` und `technik`; ohne Rolle bleibt sie unangefordert. */
  gesamt: Abo;
}

const LAEDT: AuftragsZustand = { art: 'laedt' };

const AuftraegeKontext = createContext<Wert | null>(null);

/**
 * Der Listen-Listener, gehalten ueber Seitenwechsel. Ohne Offline-Persistenz rechnet Firestore
 * jedes neue Anhaengen als brandneue Abfrage -- bis zu HOL_GRENZE Lesungen. Frueher lebte das
 * Abo in der Liste und wurde bei jedem Wechsel Liste -> Detail -> Liste neu bezahlt (Spec C,
 * Abschnitt 6, Firestore-Doku geprueft am 2026-10-01).
 *
 * Gestartet wird erst, wenn eine Seite anfordert: Wer per Link direkt auf eine Freigabe
 * springt, zahlt nichts. Die Anforderung gilt fuer die uid, fuer die sie gestellt wurde --
 * nach einem Kontowechsel ruht das Abo, bis die Liste wieder offen ist.
 */
export function AuftraegeProvider({
  quelle,
  children,
}: {
  quelle: AuftragsQuelle;
  children: ReactNode;
}) {
  const { nutzer } = useAuth();
  const uid = nutzer?.uid ?? null;
  const [angefordertFuer, setAngefordertFuer] = useState<string | null>(null);
  const aktiv = uid !== null && angefordertFuer === uid ? uid : null;
  const abo = useAuftraege(quelle, HOL_GRENZE, aktiv);
  // Ohne aktives Abo traegt der Hook fuer einen Commit noch die Liste des vorigen Kontos.
  // Die Konstante schliesst dieses Fenster, statt es dem Effekt des Hooks zu ueberlassen.
  const zustand = aktiv === null ? LAEDT : abo;

  // Kleinkram P5 (Entscheidung Tim, 2026-10-01): Steht das Abo nach einem harten Fehler, holt
  // das naechste Anfordern (die Liste wird wieder geoeffnet) es von selbst neu, so wie vor Teil C
  // der Remount. Der Knopf "Erneut versuchen" bleibt. Ueber ein Ref, damit `anfordern` stabil
  // bleibt: Seine Identitaet steuert den Effekt in `useAuftraegeKontext`.
  const zustandRef = useRef(zustand);
  useLayoutEffect(() => {
    zustandRef.current = zustand;
  }, [zustand]);

  const anfordern = useCallback(() => {
    if (!uid) return;
    setAngefordertFuer(uid);
    const jetzt = zustandRef.current;
    if (jetzt.art === 'fehler') jetzt.wiederholen();
  }, [uid]);

  // Die Gesamtansicht nach demselben Muster: erst auf Anforderung, ueber Seitenwechsel gehalten,
  // an die uid gebunden. Zusaetzlich an die Rolle: Ohne sie gaebe es nur die abgewiesene Abfrage.
  const umfang = nutzer?.gesamtUmfang ?? null;
  const [gesamtFuer, setGesamtFuer] = useState<string | null>(null);
  const gesamtAktiv = uid !== null && umfang !== null && gesamtFuer === uid ? uid : null;
  const gesamtAbo = useGesamtAuftraege(quelle, HOL_GRENZE, umfang, gesamtAktiv);
  const gesamtZustand = gesamtAktiv === null ? LAEDT : gesamtAbo;
  const gesamtRef = useRef(gesamtZustand);
  useLayoutEffect(() => {
    gesamtRef.current = gesamtZustand;
  }, [gesamtZustand]);
  const gesamtAnfordern = useCallback(() => {
    if (!uid || umfang === null) return;
    setGesamtFuer(uid);
    const jetzt = gesamtRef.current;
    if (jetzt.art === 'fehler') jetzt.wiederholen();
  }, [uid, umfang]);

  const wert = useMemo(
    () => ({ zustand, anfordern, gesamt: { zustand: gesamtZustand, anfordern: gesamtAnfordern } }),
    [zustand, anfordern, gesamtZustand, gesamtAnfordern],
  );
  return <AuftraegeKontext.Provider value={wert}>{children}</AuftraegeKontext.Provider>;
}

export function useAuftraegeKontext(): AuftragsZustand {
  const wert = useContext(AuftraegeKontext);
  if (!wert) {
    throw new Error('useAuftraegeKontext braucht einen AuftraegeProvider oberhalb im Baum.');
  }
  const { anfordern } = wert;
  // Nach jedem Kontowechsel neu, weil `anfordern` an der uid haengt.
  useEffect(() => anfordern(), [anfordern]);
  return wert.zustand;
}

/**
 * Die Gesamtansicht. `gewuenscht` statt eines eigenen Hooks je Ansicht: Die Liste ruft beide
 * Hooks immer auf (Regeln der Hooks) und fordert die Gesamtansicht nur an, wenn sie gezeigt wird.
 */
export function useGesamtAuftraegeKontext(gewuenscht: boolean): AuftragsZustand {
  const wert = useContext(AuftraegeKontext);
  if (!wert) {
    throw new Error('useGesamtAuftraegeKontext braucht einen AuftraegeProvider oberhalb im Baum.');
  }
  const { anfordern } = wert.gesamt;
  useEffect(() => {
    if (gewuenscht) anfordern();
  }, [gewuenscht, anfordern]);
  return wert.gesamt.zustand;
}
